import Decimal from 'decimal.js';

import { HandHistorySnapshot, PotGroupPlayerSnapshot } from './types';

/** One pot (or half of one) a player took, and the board that paid it. */
export interface WinAward {
  /** "Main Pot", "Side Pot 1", … — the capture's and the engines' names. */
  potName: string;
  handType?: 'Hi' | 'Lo';
  /** 1-based run on a multi-run hand; absent on a single board. */
  run?: number;
}

/** "Run 2", "Runs 1–2", "Runs 1, 3", "Runs 1–3". */
export const formatRuns = (runs: number[]): string => {
  const sorted = Array.from(new Set(runs)).sort((a, b) => a - b);
  const ranges: string[] = [];
  for (let index = 0; index < sorted.length; index++) {
    const start = sorted[index];
    let end = start;
    while (sorted[index + 1] === end + 1) end = sorted[++index];
    ranges.push(end === start ? String(start) : `${start}–${end}`);
  }
  return `${sorted.length > 1 ? 'Runs' : 'Run'} ${ranges.join(', ')}`;
};

const sidePotNumber = (potName: string) => {
  const match = potName.match(/^side pot\s*\(?(\d+)?\)?$/i);
  return match ? Number(match[1] || 1) : undefined;
};

/** "Main", or "Side" — numbered "Side 1", "Side 2" when the hand had several. */
export const shortPotName = (potName: string, sidePotCount: number): string => {
  if (/^main pot$/i.test(potName) || /^pot$/i.test(potName)) return 'Main';
  const side = sidePotNumber(potName);
  if (side !== undefined) return sidePotCount > 1 ? `Side ${side}` : 'Side';
  return potName.replace(/\s*pot$/i, '');
};

const potOrder = (potName: string) =>
  /^main pot$/i.test(potName) ? 0 : sidePotNumber(potName) ?? Number.MAX_SAFE_INTEGER;

export interface WinSummaryOptions {
  sidePotCount: number;
  hiLo?: boolean;
}

/** "Main", "Side 2", "Main Lo": the short name of the pot, or half of it, an award paid. */
const awardLabel = (award: WinAward, { sidePotCount, hiLo = false }: WinSummaryOptions) => {
  const half = hiLo && award.handType ? ` ${award.handType}` : '';
  return {
    label: `${shortPotName(award.potName, sidePotCount)}${half}`,
    order: potOrder(award.potName) * 2 + (award.handType === 'Lo' ? 1 : 0)
  };
};

/**
 * What the viewer took, pot by pot: "You won: Main (Run 1), Side (Runs 1–2)".
 * Runs appear only on a multi-run hand, Hi/Lo only on a hi-lo one. Undefined
 * when the viewer took nothing, so the caller keeps showing the pot total.
 */
export const formatWinSummary = (
  awards: WinAward[],
  options: WinSummaryOptions
): string | undefined => {
  if (awards.length === 0) return undefined;
  const groups = new Map<string, { order: number; runs: number[] }>();
  awards.forEach((award) => {
    const { label, order } = awardLabel(award, options);
    const group = groups.get(label) ?? { order, runs: [] };
    if (award.run !== undefined) group.runs.push(award.run);
    groups.set(label, group);
  });

  const parts = Array.from(groups.entries())
    .sort(([, a], [, b]) => a.order - b.order)
    .map(([label, { runs }]) => (runs.length > 0 ? `${label} (${formatRuns(runs)})` : label));
  return `You won: ${parts.join(', ')}`;
};

/** One payout of a run: who took it, from which pot (or half), and how much. */
export interface RunPayout extends WinAward {
  seat: number;
  name: string;
  amount: number;
  /** The record kept no amount for this win; `amount` is a 0 stand-in. */
  amountUnrecorded?: true;
}

/** A run header's result line: what happened on that run, and what it paid. */
export interface RunResult {
  summary: string;
  /** Every payout of that run added up — never the hand's pot. */
  amount: number;
}

/**
 * Who won a run, for a viewer who won none of it: "Aaric_G won Main, Side".
 * Pots go to one clause per set of winners, in pot order; a pot (or half) two
 * players shared reads "chopped": "Aaric_G won Main Hi; Aaric_G, Halden_T
 * chopped Main Lo".
 */
const formatRunWinners = (payouts: RunPayout[], options: WinSummaryOptions) => {
  const pots = new Map<string, { order: number; names: string[] }>();
  payouts.forEach((payout) => {
    const { label, order } = awardLabel(payout, options);
    const pot = pots.get(label) ?? { order, names: [] };
    if (!pot.names.includes(payout.name)) pot.names.push(payout.name);
    pots.set(label, pot);
  });
  const clauses = new Map<string, { names: string[]; labels: string[] }>();
  Array.from(pots.entries())
    .sort(([, a], [, b]) => a.order - b.order)
    .forEach(([label, { names }]) => {
      const key = names.join('\n');
      const clause = clauses.get(key) ?? { names, labels: [] };
      clause.labels.push(label);
      clauses.set(key, clause);
    });
  return Array.from(clauses.values())
    .map(
      ({ names, labels }) =>
        `${names.join(', ')} ${names.length > 1 ? 'chopped' : 'won'} ${labels.join(', ')}`
    )
    .join('; ');
};

/**
 * One run's result line (EN-11110 B8). The viewer — a seat, never a name — who
 * took any of the run reads "You won: …" as before; everyone else, observers
 * and guests included, reads who won it. Undefined until the run has paid.
 */
export const formatRunResult = (
  runPayouts: RunPayout[],
  viewerSeat: number | undefined,
  options: WinSummaryOptions
): RunResult | undefined => {
  if (runPayouts.length === 0) return undefined;
  // A pot nobody won the low of on this run went to the high hand whole: name
  // it without a half. The completed capture files it as Hi (builder
  // getRunAwards) while the live record says "wins the main pot", so this is
  // what keeps the two headers of one run the same.
  const splitPots = new Set(
    runPayouts.filter((payout) => payout.handType === 'Lo').map((payout) => payout.potName)
  );
  const payouts = runPayouts.map((payout) =>
    payout.handType === 'Hi' && !splitPots.has(payout.potName)
      ? { ...payout, handType: undefined }
      : payout
  );
  const viewerAwards = payouts
    .filter((payout) => viewerSeat !== undefined && viewerSeat > 0 && payout.seat === viewerSeat)
    .map(({ potName, handType }) => ({ potName, handType }));
  const summary = formatWinSummary(viewerAwards, options) ?? formatRunWinners(payouts, options);
  const amount = payouts.reduce((sum, payout) => sum + payout.amount, 0);
  return { summary, amount: Math.round(amount * 100) / 100 };
};

/** Each run's result, from its payouts. */
export const getRunResultsFromPayouts = (
  payouts: RunPayout[],
  viewerSeat: number | undefined,
  options: WinSummaryOptions
): Record<number, RunResult> =>
  Array.from(new Set(payouts.map((payout) => payout.run)))
    .filter((run): run is number => !!run)
    .sort((a, b) => a - b)
    .reduce<Record<number, RunResult>>((results, run) => {
      const result = formatRunResult(
        payouts.filter((payout) => payout.run === run),
        viewerSeat,
        options
      );
      if (result) results[run] = result;
      return results;
    }, {});

/** The pot a capture row belongs to, without the " - Hi"/" - Lo" half suffix. */
const basePotName = (groupName: string) => groupName.replace(/\s+-\s+(Hi|Lo)$/i, '');

const isSidePot = (potName: string) => sidePotNumber(potName) !== undefined;

/** Every award of a completed hand, from the payout groups or structured pots. */
const getHandPayouts = (hand: HandHistorySnapshot): RunPayout[] => {
  const won = (player: PotGroupPlayerSnapshot) =>
    player.isWinner === true && (player.grossWin ?? 1) > 0;
  const payout = (
    player: PotGroupPlayerSnapshot,
    potName: string,
    handType: WinAward['handType']
  ): RunPayout => ({
    potName,
    handType,
    run: player.run,
    seat: player.seat,
    name: player.name,
    amount: player.grossWin ?? 0,
    ...(player.grossWin === undefined ? { amountUnrecorded: true as const } : {})
  });

  if (hand.pots?.length) {
    return hand.pots.flatMap((pot) =>
      pot.awards.flatMap((award) =>
        award.players.filter(won).map((player) => payout(player, pot.name, award.handType))
      )
    );
  }
  return hand.potGroups.flatMap((group) =>
    group.players
      .filter(won)
      .map((player) =>
        payout(
          player,
          basePotName(group.name),
          player.handType ??
            (group.potType === 'lo' ? 'Lo' : group.potType === 'hi' ? 'Hi' : undefined)
        )
      )
  );
};

/** The seat's awards in a completed hand, from the payout groups or structured pots. */
export const getSeatAwards = (hand: HandHistorySnapshot, seat?: number): WinAward[] => {
  if (!seat) return [];
  return getHandPayouts(hand)
    .filter((payout) => payout.seat === seat)
    .map(({ potName, handType, run }) => ({ potName, handType, run }));
};

/** How many side pots the hand had, for numbering them. */
export const countSidePots = (hand: HandHistorySnapshot): number =>
  new Set(
    [
      ...(hand.pots ?? []).map((pot) => pot.name),
      ...hand.potGroups.map((group) => basePotName(group.name))
    ].filter(isSidePot)
  ).size;

/**
 * The completed hand's summary for the viewer — the seat the client matched to
 * its own playerGUID (attachViewerSeatToCompletedHands), never a display name —
 * optionally for one run only.
 */
export const getViewerWinSummary = (
  hand: HandHistorySnapshot,
  run?: number
): string | undefined => {
  const awards = getSeatAwards(hand, hand.tableSnapshot.viewerSeat).filter(
    (award) => run === undefined || award.run === run
  );
  return formatWinSummary(
    run === undefined ? awards : awards.map(({ run: _run, ...rest }) => rest),
    {
      sidePotCount: countSidePots(hand),
      hiLo: hand.bettingStructure.includes('HiLo')
    }
  );
};

/** What the viewer took on one run: the pots (or halves) in pot order, and their sum. */
export interface ViewerRunWin {
  pots: string[];
  amount: number;
}

/**
 * What the viewer won on each run of a multi-run hand, index 0 for Run 1 —
 * beside each run's board in the card header, so a run reads straight across
 * ("Run 3 … Main · Side 1 · Side 2 $3.13") instead of matching "(Run 3)" in a
 * pot-by-pot sentence back to its row. A run the viewer took nothing from is
 * `undefined`. The whole result is `undefined` — the caller keeps the
 * sentence — unless the viewer won something and every one of their awards
 * names a run of this hand.
 */
export const getViewerRunWins = (
  hand: HandHistorySnapshot,
  runCount: number
): (ViewerRunWin | undefined)[] | undefined => {
  const viewerSeat = hand.tableSnapshot.viewerSeat;
  if (!viewerSeat || runCount < 2) return undefined;
  const payouts = getHandPayouts(hand).filter((payout) => payout.seat === viewerSeat);
  const onBoard = (run?: number): run is number =>
    Number.isInteger(run) && (run as number) >= 1 && (run as number) <= runCount;
  if (payouts.length === 0 || !payouts.every((payout) => onBoard(payout.run))) return undefined;
  // A win with no recorded amount would show as $0.00; keep the sentence.
  if (payouts.some((payout) => payout.amountUnrecorded)) return undefined;

  const options: WinSummaryOptions = {
    sidePotCount: countSidePots(hand),
    hiLo: hand.bettingStructure.includes('HiLo')
  };
  return Array.from({ length: runCount }, (_, index) => {
    const runPayouts = payouts.filter((payout) => payout.run === index + 1);
    if (runPayouts.length === 0) return undefined;
    const pots = new Map<string, number>();
    runPayouts.forEach((payout) => {
      const { label, order } = awardLabel(payout, options);
      pots.set(label, order);
    });
    return {
      pots: Array.from(pots.entries())
        .sort(([, a], [, b]) => a - b)
        .map(([label]) => label),
      amount: runPayouts.reduce((sum, payout) => sum.plus(payout.amount), new Decimal(0)).toNumber()
    };
  });
};

/**
 * The completed hand's per-run results for the play-by-play's run headers:
 * what the viewer won on each run, else who did, and what each run paid.
 */
export const getRunResults = (hand: HandHistorySnapshot): Record<number, RunResult> =>
  getRunResultsFromPayouts(getHandPayouts(hand), hand.tableSnapshot.viewerSeat, {
    sidePotCount: countSidePots(hand),
    hiLo: hand.bettingStructure.includes('HiLo')
  });
